import { z } from 'zod/v4';
// Custom pricing rule type
export const CustomPricingRuleTypeSchema = z.enum([
    'discount-percent',
    'discount-flat',
    'override-price',
    'markup-percent',
    'markup-flat',
]);
// Custom pricing status
export const CustomPricingStatusSchema = z.enum([
    'active',
    'inactive',
    'expired',
    'scheduled',
]);
// Pricing entry (specific product/service override)
export const PricingEntrySchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid().optional(), // If null, applies to category
    productName: z.string().optional(), // Denormalized
    productCategory: z.string().optional(), // If productId is null, use category
    pricingItemId: z.string().uuid().optional(), // Specific pricing item
    pricingItemName: z.string().optional(), // Denormalized
    ruleType: CustomPricingRuleTypeSchema,
    value: z.number(), // Percent or flat amount depending on rule type
    minQuantity: z.number().optional(), // Only applies above this quantity
    maxQuantity: z.number().optional(), // Only applies below this quantity
    notes: z.string().optional(),
});
// Custom pricing master record (pricing rule for an account)
export const CustomPricingSchema = z.object({
    id: z.string().uuid(),
    name: z.string(), // e.g., "Anderson Law Firm - Volume Discount"
    description: z.string().optional(),
    // Associated account(s)
    accountId: z.string().uuid(),
    accountName: z.string().optional(), // Denormalized
    accountNumber: z.string().optional(), // Denormalized
    // Pricing entries (the actual rules)
    entries: z.array(PricingEntrySchema).default([]),
    // Date range (for promotional periods)
    effectiveDate: z.string().datetime(),
    expirationDate: z.string().datetime().optional(), // Null = no expiration
    // Priority (higher = takes precedence)
    priority: z.number().default(0),
    // Flags
    applyToAllProducts: z.boolean().default(false),
    stackable: z.boolean().default(false), // Can combine with other discounts
    // Notes
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    // Status
    status: CustomPricingStatusSchema.default('active'),
    // Audit
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateCustomPricingSchema = CustomPricingSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateCustomPricingSchema = CustomPricingSchema.partial().omit({
    id: true,
    createdAt: true,
});
// Custom pricing preview
export const CustomPricingPreviewSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    accountName: z.string().optional(),
    accountNumber: z.string().optional(),
    entryCount: z.number(),
    effectiveDate: z.string().datetime(),
    expirationDate: z.string().datetime().optional(),
    status: CustomPricingStatusSchema,
});
